import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';

/**
 * UserAvatar Component Props
 */
interface UserAvatarProps {
  /** URL of the user's avatar image (optional) */
  avatarUrl?: string | null;
  /** Hex color code for the avatar background (e.g., '#EF4444') */
  avatarColor?: string | null;
  /** User's name (used to generate initials if no avatar image) */
  name?: string | null;
  /** Size of the avatar (Tailwind classes) */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Additional CSS classes */
  className?: string;
}

/**
 * Get user's initials from their name
 */
const getInitials = (name?: string | null): string => {
  if (!name) return '?';

  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    // First initial + last initial
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  // Just first character
  return name[0].toUpperCase();
};

/**
 * Get size classes for the avatar
 */
const getSizeClasses = (size: 'xs' | 'sm' | 'md' | 'lg' | 'xl') => {
  switch (size) {
    case 'xs':
      return { container: 'h-5 w-5', text: 'text-[0.5rem]' };
    case 'sm':
      return { container: 'h-8 w-8', text: 'text-xs' };
    case 'md':
      return { container: 'h-10 w-10', text: 'text-sm' };
    case 'lg':
      return { container: 'h-12 w-12', text: 'text-base' };
    case 'xl':
      return { container: 'h-24 w-24', text: 'text-5xl' };
    default:
      return { container: 'h-10 w-10', text: 'text-sm' };
  }
};

/**
 * UserAvatar Component
 * Displays a user's avatar with either:
 * 1. Their uploaded photo (if avatarUrl is provided)
 * 2. Their initials on a colored background (if no photo)
 *
 * Used throughout the portal for consistent avatar display.
 */
export function UserAvatar({
  avatarUrl,
  avatarColor = '#EF4444', // Default to red
  name,
  size = 'md',
  className = '',
}: UserAvatarProps) {
  const { container, text } = getSizeClasses(size);
  const initials = getInitials(name);

  return (
    <Avatar className={`${container} ${className}`}>
      {avatarUrl && <AvatarImage src={avatarUrl} alt={name || 'User avatar'} />}
      <AvatarFallback
        className={`font-semibold text-white ${text}`}
        style={{
          backgroundColor: avatarColor || '#EF4444',
          color: 'white',
        }}
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}
